import { useCourses } from 'core/hooks/course/useCourses';
import { useGroups } from 'core/hooks/useGroups';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type SelectOption = {
  label: string;
  value: string;
};

type UseTeacherAIAnalyticsPanelReturn = {
  classOptions: SelectOption[];
  selectedClass: SelectOption;
  setSelectedClass: (option: SelectOption) => void;
  timePeriodOptions: SelectOption[];
  selectedTimePeriod: SelectOption;
  setSelectedTimePeriod: (option: SelectOption) => void;
  allCourseIds: string[];
  groupCourseMap: Record<string, string[]>;
  loading: boolean;
};

function useTeacherAIAnalyticsPanel(): UseTeacherAIAnalyticsPanelReturn {
  const { t } = useTranslation(['chatbot']);
  const { groups } = useGroups();
  const { courses } = useCourses();

  const allClassesOption: SelectOption = useMemo(
    () => ({
      label: t('chatbot:ai_analytics_all_classes'),
      value: 'all',
    }),
    [t]
  );

  const classOptions: SelectOption[] = useMemo(() => {
    const schoolGroups = groups?.schoolGroups || [];
    const groupOptions = schoolGroups.map((group: { name: string; guid: string }) => ({
      label: group.name,
      value: group.guid,
    }));
    return [allClassesOption, ...groupOptions];
  }, [groups, allClassesOption]);

  const [selectedClass, setSelectedClass] = useState<SelectOption>(allClassesOption);

  const timePeriodOptions: SelectOption[] = useMemo(
    () => [
      { label: t('chatbot:ai_analytics_period_last_week'), value: '7d' },
      { label: t('chatbot:ai_analytics_period_last_month'), value: '30d' },
      { label: t('chatbot:ai_analytics_period_last_3_months'), value: '3m' },
    ],
    [t]
  );

  const [selectedTimePeriod, setSelectedTimePeriod] = useState<SelectOption>(timePeriodOptions[0]);

  const groupGuids = useMemo(() => {
    const schoolGroups = groups?.schoolGroups || [];
    return schoolGroups.map((g: { guid: string }) => g.guid);
  }, [groups]);

  const groupCourseMap: Record<string, string[]> = useMemo(() => {
    if (!courses || groupGuids.length === 0) return {};
    const map: Record<string, string[]> = {};
    groupGuids.forEach((gid: string) => {
      map[gid] = courses
        .filter((c: { school_group_guid: string }) => c.school_group_guid === gid)
        .map((c: { guid: string }) => c.guid);
    });
    return map;
  }, [courses, groupGuids]);

  const allCourseIds: string[] = useMemo(() => {
    return Object.values(groupCourseMap).flat();
  }, [groupCourseMap]);

  const loading = !groups || !courses;

  return {
    classOptions,
    selectedClass,
    setSelectedClass,
    timePeriodOptions,
    selectedTimePeriod,
    setSelectedTimePeriod,
    allCourseIds,
    groupCourseMap,
    loading,
  };
}

export default useTeacherAIAnalyticsPanel;
